// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	type FormInputs,
	MAX_SLOWMODE_SECONDS,
	SLOWMODE_CUSTOM_SENTINEL,
} from '@app/features/channel/components/modals/channel_tabs/channel_overview_tab/shared';
import {CUSTOM_ELLIPSIS_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {Input} from '@app/features/ui/components/form/FormInput';
import {Select as FormSelect} from '@app/features/ui/components/form/FormSelect';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import type React from 'react';
import {useMemo} from 'react';
import {Controller, type UseFormReturn} from 'react-hook-form';

const SECONDS_DESCRIPTOR = msg({
	message: '{seconds} seconds',
	comment:
		'Channel overview settings tab label, control, or validation message (name, topic, slowmode, voice region, mature content gate).',
});
const MINUTES_DESCRIPTOR = msg({
	message: '{minutes} minutes',
	comment:
		'Channel overview settings tab label, control, or validation message (name, topic, slowmode, voice region, mature content gate).',
});
const HOURS_DESCRIPTOR = msg({
	message: '{hours} hours',
	comment:
		'Channel overview settings tab label, control, or validation message (name, topic, slowmode, voice region, mature content gate).',
});
const OFF_DESCRIPTOR = msg({
	message: 'Off',
	comment:
		'Channel overview settings tab label, control, or validation message (name, topic, slowmode, voice region, mature content gate).',
});
const MINUTE_DESCRIPTOR = msg({
	message: '{oneMinute} minute',
	comment:
		'Channel overview settings tab label, control, or validation message (name, topic, slowmode, voice region, mature content gate).',
});
const HOUR_DESCRIPTOR = msg({
	message: '{oneHour} hour',
	comment:
		'Channel overview settings tab label, control, or validation message (name, topic, slowmode, voice region, mature content gate).',
});
const SLOWMODE_DESCRIPTOR = msg({
	message: 'Slowmode',
	comment:
		'Channel overview settings tab label, control, or validation message (name, topic, slowmode, voice region, mature content gate).',
});
const MEMBERS_MUST_WAIT_BETWEEN_MESSAGES_DESCRIPTOR = msg({
	message: 'Members must wait between messages.',
	comment:
		'Channel overview settings tab label, control, or validation message (name, topic, slowmode, voice region, mature content gate).',
});
const CUSTOM_SLOWMODE_SECONDS_DESCRIPTOR = msg({
	message: 'Custom slowmode (seconds)',
	comment:
		'Channel overview settings tab label, control, or validation message (name, topic, slowmode, voice region, mature content gate).',
});
const ANY_VALUE_FROM_0_TO_SECONDS_DESCRIPTOR = msg({
	message: 'Any value from 0 to {maxSlowmodeSeconds} seconds.',
	comment:
		'Channel overview settings tab label, control, or validation message (name, topic, slowmode, voice region, mature content gate).',
});

export function useSlowmodeOptions(): Array<{value: number; label: string}> {
	const {i18n} = useLingui();
	return useMemo(() => {
		const secondsLabel = (seconds: number) => i18n._(SECONDS_DESCRIPTOR, {seconds});
		const minutesLabel = (minutes: number) => i18n._(MINUTES_DESCRIPTOR, {minutes});
		const hoursLabel = (hours: number) => i18n._(HOURS_DESCRIPTOR, {hours});
		const oneMinute = 1;
		const oneHour = 1;
		return [
			{value: 0, label: i18n._(OFF_DESCRIPTOR)},
			{value: 5, label: secondsLabel(5)},
			{value: 10, label: secondsLabel(10)},
			{value: 15, label: secondsLabel(15)},
			{value: 30, label: secondsLabel(30)},
			{value: 60, label: i18n._(MINUTE_DESCRIPTOR, {oneMinute})},
			{value: 120, label: minutesLabel(2)},
			{value: 300, label: minutesLabel(5)},
			{value: 600, label: minutesLabel(10)},
			{value: 900, label: minutesLabel(15)},
			{value: 1800, label: minutesLabel(30)},
			{value: 3600, label: i18n._(HOUR_DESCRIPTOR, {oneHour})},
			{value: 7200, label: hoursLabel(2)},
			{value: 21600, label: hoursLabel(6)},
			{value: SLOWMODE_CUSTOM_SENTINEL, label: i18n._(CUSTOM_ELLIPSIS_DESCRIPTOR)},
		];
	}, [i18n]);
}

interface SlowmodeControlProps {
	form: UseFormReturn<FormInputs>;
	slowmodeOptions: Array<{value: number; label: string}>;
	isSlowmodeCustom: boolean;
	setIsSlowmodeCustom: (value: boolean) => void;
}

export const SlowmodeControl: React.FC<SlowmodeControlProps> = ({
	form,
	slowmodeOptions,
	isSlowmodeCustom,
	setIsSlowmodeCustom,
}) => {
	const {i18n} = useLingui();
	return (
		<Controller
			name="slowmode"
			control={form.control}
			render={({field}) => {
				const currentValue = field.value ?? 0;
				const selectValue = isSlowmodeCustom ? SLOWMODE_CUSTOM_SENTINEL : currentValue;
				return (
					<>
						<FormSelect<number>
							label={i18n._(SLOWMODE_DESCRIPTOR)}
							description={i18n._(MEMBERS_MUST_WAIT_BETWEEN_MESSAGES_DESCRIPTOR)}
							value={selectValue}
							options={slowmodeOptions}
							onChange={(v) => {
								if (v === SLOWMODE_CUSTOM_SENTINEL) {
									setIsSlowmodeCustom(true);
									return;
								}
								setIsSlowmodeCustom(false);
								field.onChange(v);
							}}
							data-flx="channel.channel-tabs.channel-overview-tab.form-select.set-is-slowmode-custom"
						/>
						{isSlowmodeCustom && (
							<Input
								type="number"
								label={i18n._(CUSTOM_SLOWMODE_SECONDS_DESCRIPTOR)}
								footer={i18n._(ANY_VALUE_FROM_0_TO_SECONDS_DESCRIPTOR, {
									maxSlowmodeSeconds: MAX_SLOWMODE_SECONDS,
								})}
								min={0}
								max={MAX_SLOWMODE_SECONDS}
								step={1}
								value={String(currentValue)}
								onChange={(event) => {
									const raw = event.target.value;
									if (raw === '') {
										field.onChange(0);
										return;
									}
									const parsed = Number.parseInt(raw, 10);
									if (Number.isNaN(parsed)) return;
									const clamped = Math.max(0, Math.min(MAX_SLOWMODE_SECONDS, parsed));
									field.onChange(clamped);
								}}
								data-flx="channel.channel-tabs.channel-overview-tab.input.change.number"
							/>
						)}
					</>
				);
			}}
			data-flx="channel.channel-tabs.channel-overview-tab.controller"
		/>
	);
};
